<template>
  <div id="account-partner">
    <v-card class="pl-8 pr-10 pt-8 pb-8 mt-4 mb-5 elevation-0">
      <div v-if="type === 'referral'" class="mb-8">
        <h1 class="card-title">
          <v-icon size="16" color="secondary" class="account-partner-title-icon">
            mdi-gift-outline
          </v-icon>
          {{ $t(`views.myAccount.partner.invite.title`) }}
        </h1>
        <v-card
          class="account-partner-description pa-5 mb-0 mt-2 elevation-0"
          color="softBackgroundGrey"
        >
          <ul>
            <li>{{ $t(`views.myAccount.partner.invite.li1`) }}</li>
            <li>{{ $t(`views.myAccount.partner.invite.li2`) }}</li>
            <li>{{ $t(`views.myAccount.partner.invite.li3`) }}</li>
          </ul>
        </v-card>
      </div>

      <div class="d-flex flex-wrap">
        <div class="mr-12 mb-5" @click="copyItem(userCode)">
          <h1 class="card-title mb-3">{{ $t(`views.myAccount.partner.code.${type}`) }}</h1>
          <v-chip
            :color="$vuetify.theme.dark ? 'dark_backgroundDarkBlue' : 'softBackgroundGrey'"
            class="account-partner-chip"
            label
          >
            <span class="partner-url">{{ userCode }}</span>
            <v-divider class="mx-4" vertical light></v-divider>
            <span>
              <v-icon size="16">mdi-content-copy</v-icon>
              <span class="mx-1">{{ $t('common.copy') }}</span>
            </span>
          </v-chip>
        </div>

        <div @click="copyItem(partnerUrl)">
          <h1 class="card-title mb-3">{{ $t('views.myAccount.partner.link') }}</h1>
          <v-chip
            :color="$vuetify.theme.dark ? 'dark_backgroundDarkBlue' : 'softBackgroundGrey'"
            class="account-partner-chip"
            label
          >
            <span class="partner-url">{{ partnerUrl }}</span>
            <v-divider class="mx-4" vertical light></v-divider>
            <span>
              <v-icon size="16">mdi-content-copy</v-icon>
              <span class="mx-1">{{ $t('common.copy') }}</span>
            </span>
          </v-chip>
        </div>
      </div>
    </v-card>

    <v-card class="pl-8 pr-10 pt-8 pb-10 mt-4 mb-5 elevation-0">
      <h1 class="card-title mb-3">{{ $t('views.myAccount.partner.referredUsers') }}</h1>

      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        sort-by="createdAt"
        :sort-desc="true"
        class="account-partner-users-table elevation-0 mt-2"
        mobile-breakpoint="0"
        :loading="loading"
        :footer-props="{
          'items-per-page-options': defaultPaginationItemsPerPageOptions,
        }"
        :items-per-page="20"
        dense
        @update:options="loadItems"
      >
        <template v-slot:item.status="{ item }">
          <span
            v-if="
              ['PLAN_PREMIUM', 'PLAN_PRO', 'PLAN_UNLIMITED', 'PLAN_CUSTOM'].indexOf(item.status) >
              -1
            "
          >
            <span class="status-subscription">
              {{ $t(`views.myAccount.billing.status.${item.status}`) }}
            </span>
          </span>
          <span v-else>
            {{ $t(`views.myAccount.billing.status.${item.status}`) }}
          </span>
        </template>

        <template v-slot:item.interval="{ item }">
          {{ item.interval ? $t(`views.myAccount.partner.interval.${item.interval}`) : '' }}
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ getDateUserFullFormat(item.createdAt) }}
        </template>

        <template v-slot:item.lastSignIn="{ item }">
          {{
            isInLast24h(item.lastSignIn)
              ? $t(`views.myAccount.partner.lastSignInLast24h`)
              : getDateUserFormat(item.lastSignIn)
          }}
        </template>

        <template v-slot:item.referralCompleted="{ item }">
          <span v-if="item.referralCompleted">
            <v-chip small color="softBackgroundGreen">
              {{ $t(`views.myAccount.partner.referralCompleted.completed`) }}
            </v-chip>
          </span>
          <span v-else>
            <v-chip small color="softBackgroundGrey">
              {{ $t(`views.myAccount.partner.referralCompleted.pending`) }}
            </v-chip>
          </span>
        </template>
      </v-data-table>
    </v-card>

    <v-card v-if="type === 'referral'" class="referral-partner pa-7 pb-3 mb-0 mt-10 elevation-0">
      <div>
        <!-- <v-icon
          size="19"
          :color="$vuetify.theme.dark ? 'white' : 'secondary'"
          class="referral-partner-title-icon"
        >
          mdi-cash
        </v-icon> -->
        <span
          class="referral-partner-title"
          :class="{ 'white--text': $vuetify.theme.dark, 'secondary--text': !$vuetify.theme.dark }"
        >
          {{ $t(`views.myAccount.partner.referralPartner.title`) }}
        </span>
      </div>
      <p class="referral-partner-description mt-1">
        {{ $t(`views.myAccount.partner.referralPartner.description`) }}
        <a
          target="_blank"
          href="https://www.filios.app/contacta"
          :class="{ 'white--text': $vuetify.theme.dark, 'secondary--text': !$vuetify.theme.dark }"
        >
          {{ $t(`views.myAccount.partner.referralPartner.contact`) }}
        </a>
      </p>
    </v-card>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getReferredUsers } from '@/lib/api.js'
import tableMixin from '@/views/components/core/tableMixin.js'
import { getDateUserFullFormat, getDateUserFormat, isInLast24h } from '@/lib/utils/date.js'

export default {
  components: {},
  mixins: [tableMixin],
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  data: () => {
    return {
      loading: false,
    }
  },
  computed: {
    ...mapState(['user']),
    userCode() {
      return this.user.code
    },
    partnerUrl() {
      return `https://go.filios.app/#/signup?referralCode=${this.user.code}`
    },
    defaultHeaders() {
      const defaultHeaders = [
        {
          text: this.$t('views.myAccount.partner.createdAt'),
          value: 'createdAt',
        },
        {
          text: this.$t('views.myAccount.partner.lastSignIn'),
          value: 'lastSignIn',
        },
        {
          text: this.$t('views.myAccount.partner.email'),
          value: 'email',
        },
        {
          text: this.$t('views.myAccount.partner.status'),
          value: 'status',
        },
        {
          text: this.$t('views.myAccount.partner.interval.title'),
          value: 'interval',
        },
        {
          text: this.$t('views.myAccount.partner.trialDays'),
          value: 'trialDays',
        },
      ]

      if (this.type === 'referral') {
        defaultHeaders.push({
          text: this.$t('views.myAccount.partner.referralCompleted.title'),
          value: 'referralCompleted',
        })
      }

      return defaultHeaders
    },
  },
  methods: {
    getDateUserFullFormat,
    getDateUserFormat,
    isInLast24h,
    async loadItems() {
      this.loading = true
      try {
        const filter = {}
        const { page, limit, sort } = this.getTablePaginationOptions(this.options)
        const response = await getReferredUsers({ filter, page, limit, sort })
        this.setTablePaginatedResponse(response)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loading = false
    },
    async copyItem(item) {
      await navigator.clipboard.writeText(item)
      this.$root.$snackbar.success({ text: this.$t('common.copyOk') })
    },
  },
}
</script>

<style lang="scss">
#account-partner {
  .account-partner-chip {
    cursor: pointer;
  }
  h2 {
    font-size: 13px;
    font-weight: 600;
  }
  .account-partner-title-icon {
    margin-bottom: 4px;
    margin-right: 4px;
  }
  .account-partner-description {
    font-size: 13px;
  }
  .status-subscription {
    font-weight: 600;
  }
  .referral-partner {
    .referral-partner-title-icon {
      margin-bottom: 2px;
    }
    .referral-partner-title {
      font-size: 13px;
      font-weight: 600;
    }
    .referral-partner-description {
      font-size: 13px;
      font-weight: 300;
    }
  }
}
</style>
